import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { scoreOnboardingTestApi } from "./scoreOnboardingTest.api";
import {
	CeoSplitInfo,
	OnboardingSampleStatus,
	OnboardingTestResult,
	OnboardingTestSuggestion,
	ScoreTestAnalysisStatus,
} from "./scoreOnboardingTest.schema";

export interface ScoreOnboardingTestState {
	/** The ~10 scored sample leads of the Testing sub-stage. */
	results: OnboardingTestResult[];
	/** Result row ids the user manually corrected in this test round. */
	changedResultIds: number[];
	/** AI refinement suggestions drafted from the corrections. */
	suggestions: OnboardingTestSuggestion[];
	/** Polling state of the analysis job. */
	analysisStatus: ScoreTestAnalysisStatus;
	/** Whether the sample is still being built — the list grows while it is. */
	sampleStatus: OnboardingSampleStatus;
	/** CEO scoring split state (SCORING sub-stage question). */
	ceoSplit: CeoSplitInfo | null;
}

const initialState: ScoreOnboardingTestState = {
	results: [],
	changedResultIds: [],
	suggestions: [],
	analysisStatus: ScoreTestAnalysisStatus.IDLE,
	sampleStatus: OnboardingSampleStatus.BUILDING,
	ceoSplit: null,
};

export const scoreOnboardingTestSlice = createSlice({
	name: "scoreOnboardingTest",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.getOnboardingTestResults.matchFulfilled,
			(state, { payload }) => {
				state.results = payload.data;
				state.sampleStatus = payload.status;
				// Corrections are NOT cleared here: while the sample is still building this
				// endpoint is polled every 10s, and a correction the user just made must not
				// vanish under them. They are cleared when they are consumed — see the apply
				// matcher below.
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.rebuildOnboardingTestSample.matchFulfilled,
			(state, { payload }) => {
				// Back to BUILDING immediately: the retry flow flips the stage to its loading
				// phase, and that phase's settle-effect must not read the stale READY of the
				// empty build the user just discarded.
				state.results = payload.data;
				state.sampleStatus = payload.status;
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.updateOnboardingTestResult.matchFulfilled,
			(state, { payload }) => {
				const resultId = payload.scorePersonaCategoryResultId;

				if (!state.changedResultIds.includes(resultId)) {
					state.changedResultIds.push(resultId);
				}

				const row = state.results.find((result) => result.id === resultId);

				if (row && payload.newOverallScore) {
					row.overallScore = payload.newOverallScore;
				}
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.analyzeOnboardingTestChanges.matchFulfilled,
			(state, { payload }) => {
				state.analysisStatus = payload.status;
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.getOnboardingTestSuggestions.matchFulfilled,
			(state, { payload }) => {
				state.analysisStatus = payload.status;
				state.suggestions = payload.suggestions;
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.reviewOnboardingTestSuggestion.matchFulfilled,
			(state, { payload }) => {
				state.suggestions = state.suggestions.map((suggestion) =>
					suggestion.id === payload.id ? payload : suggestion
				);
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.applyOnboardingTestSuggestions.matchFulfilled,
			(state, { payload }) => {
				state.results = payload.data;
				state.changedResultIds = [];
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.getCeoSplit.matchFulfilled,
			(state, { payload }) => {
				state.ceoSplit = payload;
			}
		);
		builder.addMatcher(
			scoreOnboardingTestApi.endpoints.createCeoSplit.matchFulfilled,
			(state, { payload }) => {
				state.ceoSplit = payload;
			}
		);
	},
});

export const scoreOnboardingTestReducer = scoreOnboardingTestSlice.reducer;

export const scoreOnboardingTestSelector = (state: RootState): ScoreOnboardingTestState =>
	state[scoreOnboardingTestSlice.name];

export const selectOnboardingTestResults = (state: RootState) =>
	scoreOnboardingTestSelector(state).results;

export const selectOnboardingTestChangedResultIds = (state: RootState) =>
	scoreOnboardingTestSelector(state).changedResultIds;

export const selectOnboardingTestSuggestions = (state: RootState) =>
	scoreOnboardingTestSelector(state).suggestions;

export const selectOnboardingTestAnalysisStatus = (state: RootState) =>
	scoreOnboardingTestSelector(state).analysisStatus;

export const selectOnboardingSampleStatus = (state: RootState): OnboardingSampleStatus =>
	scoreOnboardingTestSelector(state).sampleStatus;

export const selectCeoSplit = (state: RootState) => scoreOnboardingTestSelector(state).ceoSplit;
